import { coerceNumberProperty } from '@angular/cdk/coercion';
import { DestroyRef, Directive, effect, ElementRef, inject, INJECTOR, input, OnInit, output } from '@angular/core';
import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { Deferred } from '../utilities/deferred';
import { isServer } from '../utilities/platform-type';
import { SwiperStyleInjectorService } from './swiper-style-injector.service';
import { SwiperModuleId } from './utils';

@Directive({
  standalone: true,
  selector: 'swiper',
  exportAs: 'SwiperDirective',
  host: {
    class: 'swiper'
  }
})
export class SwiperDirective implements OnInit {
  private readonly dRef = inject(DestroyRef);
  private readonly injector = inject(INJECTOR);
  private readonly hostElement = inject(ElementRef).nativeElement as HTMLElement;
  private readonly swiperStyle = inject(SwiperStyleInjectorService);
  private readonly isServer = isServer();

  private _swiper?: Swiper;
  public get swiper() {
    if (!this._swiper)
      throw new Error('swiper not initialized');
    return this._swiper;
  }

  private readonly _initialized = new Deferred();
  public readonly initialized = this._initialized as Promise<void>;

  public readonly options$ = input<SwiperOptions>({}, { alias: 'options' });
  public readonly modules$ = input<readonly SwiperModuleId[]>([], { alias: 'modules' });

  public readonly realIndex$ = input(0, { alias: 'realIndex' });
  public readonly realIndexChange = output<number>();

  private destroyed = false;


  ngOnInit(): void {
    if (this.isServer)
      return;

    this.dRef.onDestroy(() => {
      /** 
       * Question:
       * Why do we need to maintain a `destroyed` property?
       * 
       * Answer:
       * Originally, we would simply call `swiper.destroy()` 
       * and be done with it. However, when calling `swiper.destroy()`,
       * swiper internally destroys the loop it has created for the slides.
       * This causes swiper to emit the `realIndexChange` event.
       * Our directive has a `realIndex$` ouput which mirrors that event,
       * via an event handler registered to the swiper instance.
       * Since the directive is being destroyed, that output ref is no longer available, 
       * and the event handler errors out.
       * Therefore, we need to set the `destroyed` property, allowing event handlers to check
       * before they run their logic.
       */
      this.destroyed = true;
      this._swiper?.destroy();
    });

    effect(async () => {
      const options = this.options$();
      const modules = this.modules$();

      this._swiper?.destroy();
      await this.swiperStyle.injectModules(options, modules);
      this.initSwiper();
    }, { injector: this.injector });
  }

  private initSwiper() {
    this._swiper = new Swiper(this.hostElement, this.options$());

    const isLooped = (() => {
      const options = this.options$();
      return !!options.loop;
    })();

    const inputRealIndexEffect = effect(() => {
      const realIndex = this.realIndex$();
      if (realIndex === this.swiper.realIndex)
        return;
      if (isLooped)
        this.swiper.slideToLoop(realIndex);
      else
        this.swiper.slideTo(realIndex);
    }, { injector: this.injector });

    this._swiper.on('realIndexChange', (swiper) => {
      if (this.destroyed) return;
      this.realIndexChange.emit(swiper.realIndex);
    });

    this.swiper.on('destroy', () => {
      inputRealIndexEffect.destroy();
    });

    this._initialized.resolve();
  }
}

export const NgSwiperModule = [
  SwiperDirective
] as const;